import { roomGameCatalog } from "./roomGameCatalog";

interface WebMcpTool {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  annotations: { readOnlyHint: true; destructiveHint: false; idempotentHint: true };
  execute: (input: Record<string, unknown>) => Promise<WebMcpResult>;
}

interface WebMcpResult {
  content: Array<{ type: "text"; text: string }>;
  structuredContent: unknown;
}

interface WebMcpContext {
  registerTool?: (tool: WebMcpTool, options?: { signal?: AbortSignal }) => void | Promise<void>;
  provideContext?: (
    context: { tools: WebMcpTool[] },
    options?: { signal?: AbortSignal },
  ) => void | Promise<void>;
}

const readOnlyAnnotations = {
  readOnlyHint: true,
  destructiveHint: false,
  idempotentHint: true,
} as const;

function result(value: unknown): WebMcpResult {
  return {
    content: [{ type: "text", text: JSON.stringify(value, null, 2) }],
    structuredContent: value,
  };
}

export const topzleWebMcpTools: WebMcpTool[] = [
  {
    name: "list_topzle_games",
    description:
      "List playable Topzle games with player counts, AI availability, and authoritative guide URLs.",
    inputSchema: { type: "object", properties: {}, additionalProperties: false },
    annotations: readOnlyAnnotations,
    execute: async () =>
      result(
        roomGameCatalog.map((game) => ({
          ...game,
          guideUrl: `https://topzle.com/how-to-play/${game.id}`,
        })),
      ),
  },
  {
    name: "get_topzle_game",
    description: "Get public catalog details and an authoritative guide URL for one Topzle game.",
    inputSchema: {
      type: "object",
      properties: {
        gameId: { type: "string", enum: roomGameCatalog.map((game) => game.id) },
      },
      required: ["gameId"],
      additionalProperties: false,
    },
    annotations: readOnlyAnnotations,
    execute: async (input) => {
      const game = roomGameCatalog.find((candidate) => candidate.id === input.gameId);
      if (!game)
        return result({
          error: "unknown_game",
          availableGameIds: roomGameCatalog.map((candidate) => candidate.id),
        });
      return result({
        ...game,
        guideUrl: `https://topzle.com/how-to-play/${game.id}`,
        playUrl: "https://topzle.com/sign-in",
      });
    },
  },
  {
    name: "get_topzle_platform_info",
    description:
      "Get Topzle's public product description, safety links, and canonical agent discovery URLs.",
    inputSchema: { type: "object", properties: {}, additionalProperties: false },
    annotations: readOnlyAnnotations,
    execute: async () =>
      result({
        name: "Topzle",
        description:
          "A social gaming platform where friends and family can play, talk, and stay together in one room.",
        website: "https://topzle.com/",
        games: "https://topzle.com/games",
        guides: "https://topzle.com/how-to-play",
        safety: "https://topzle.com/safety",
        privacy: "https://topzle.com/privacy",
        terms: "https://topzle.com/terms",
        apiCatalog: "https://topzle.com/.well-known/api-catalog",
        mcpServerCard: "https://topzle.com/.well-known/mcp/server-card.json",
        agentSkills: "https://topzle.com/.well-known/agent-skills/index.json",
      }),
  },
];

export function registerTopzleWebMcpTools(
  contexts: Array<WebMcpContext | undefined>,
): AbortController | null {
  const context = contexts.find(
    (candidate) => candidate?.registerTool || candidate?.provideContext,
  );
  if (!context) return null;

  const controller = new AbortController();
  if (context.registerTool) {
    for (const tool of topzleWebMcpTools) {
      const registration = context.registerTool(tool, { signal: controller.signal });
      if (registration instanceof Promise) registration.catch(() => undefined);
    }
  } else {
    const registration = context.provideContext?.(
      { tools: topzleWebMcpTools },
      { signal: controller.signal },
    );
    if (registration instanceof Promise) registration.catch(() => undefined);
  }
  return controller;
}

export function installTopzleWebMcp(): void {
  const documentContext = (document as Document & { modelContext?: WebMcpContext }).modelContext;
  const navigatorContext = (navigator as Navigator & { modelContext?: WebMcpContext }).modelContext;
  const controller = registerTopzleWebMcpTools([documentContext, navigatorContext]);
  if (controller) window.addEventListener("pagehide", () => controller.abort(), { once: true });
}
